import type { SnakeCase } from "type-fest";
import type { ZodType } from "zod";
import z from "zod";

import { looseUuid } from "@thalamos/common";

import {
  OrganisationIdSchema,
  type OrganisationId,
  type OrganisationIdUnbranded,
} from "./organisation.js";

export type UserIdUnbranded = string;
export const UserIdSchema =
  looseUuid().brand<"haven-common:UserId">() satisfies ZodType<UserIdUnbranded>;
/** A person who signs in to Haven — anybody holding a membership anywhere. */
export type UserId = z.infer<typeof UserIdSchema>;

export type EmailAddressUnbranded = string;
export const EmailAddressSchema = z
  .email()
  .brand<"haven-common:EmailAddress">() satisfies ZodType<EmailAddressUnbranded>;
/**
 * An email address. Two that differ only in case are the same address, so
 * compare them case-insensitively; the schema does not normalise case.
 */
export type EmailAddress = z.infer<typeof EmailAddressSchema>;

/**
 * A membership's role.
 *
 * A closed set, so an unrecognised role fails to parse rather than flowing
 * through the app and arriving in a picker. The column stays a plain string:
 * admitting another role needs no migration, so the schema is the narrowing
 * rather than the storage.
 *
 * Written out as a union as well as a schema because tsoa follows the type into
 * the API contract but cannot follow `z.infer`. A role added to one and not the
 * other fails the first time it is used, rather than quietly widening anything.
 *
 * `thalamos_admin` is not a role at a customer: it confers administrative reach
 * only when held at the Thalamos organisation, which
 * `isThalamosAdminMembership` checks both halves of.
 *
 * `organisation_admin` is administrative work at an organisation — opening a
 * record, allocating who will run the case, merging duplicates — and is
 * qualified rather than plain, because a bare "admin" beside `thalamos_admin`
 * reads as the same thing and is not. `responsible_user` makes a user eligible
 * to run a person's case and decide who else is involved, sharing its word with
 * that involvement as `assessor`, `approver` and `viewer` do; holding the role
 * makes nobody responsible for anybody. `approver`
 * reviews a designated assessor's work; it neither runs the case nor assesses.
 * `viewer` only reads, and is eligible to be named viewer and nothing else, so
 * that giving somebody sight of a child does not offer them for any other job.
 */
export type Role =
  | "assessor"
  | "responsible_user"
  | "approver"
  | "viewer"
  | "organisation_admin"
  | "thalamos_admin";
export const RoleSchema = z.enum([
  "assessor",
  "responsible_user",
  "approver",
  "viewer",
  "organisation_admin",
  "thalamos_admin",
]) satisfies ZodType<Role & SnakeCase<Role>>;

/**
 * A role a user holds at an organisation (`docs/glossary/membership.md`, the
 * user's sense). An organisation's membership of another is an organisation
 * relationship, never this. What a session acts in is a session context, which
 * may combine several of a user's memberships at one organisation.
 *
 * One shape, because one is all the domain has shown it needs. A user holding
 * two roles at the same organisation, or the same role at two organisations,
 * both fall out of this — what does not is a subdivision of an organisation,
 * which would be a schema change and should be, rather than something a
 * speculative second variant here quietly permits.
 */
export type UserMembershipUnbranded = {
  organisationId: OrganisationIdUnbranded;
  role: Role;
  organisationName?: string | undefined;
};

export const UserMembershipSchema = z.object({
  organisationId: OrganisationIdSchema,
  role: RoleSchema,
  organisationName: z.string().min(1).max(255).optional(),
}) satisfies ZodType<UserMembershipUnbranded>;

export type UserMembership = z.infer<typeof UserMembershipSchema>;

/** The id a membership is chosen by. */
export const membershipId = (membership: UserMembership): OrganisationId =>
  membership.organisationId;

/**
 * How an organisation is named to a user. Falls back to the id so a picker
 * never renders a row the user cannot tell apart from another.
 */
export const membershipLabel = (
  membership: Pick<UserMembership, "organisationId" | "organisationName">,
): string => membership.organisationName ?? membership.organisationId;

/**
 * The kind of work a session does at an organisation.
 *
 * Case work combines every role that does a job on a child's case, so somebody
 * who both runs a case and assesses does both without switching. Organisation
 * administration and Thalamos administration each stand alone: neither is part
 * of a case, and a case-work role must not borrow their reach. See
 * `docs/decisions/2026-09-23-authority-comes-from-every-role-held-at-the-acting-organisation.md`.
 */
export type SessionContextKind =
  "case-work" | "organisation_administration" | "thalamos_administration";
// TODO(case-work): respell `case-work`, and give the set the SnakeCase guard
// `RoleSchema` has, once "case" is settled.
export const SessionContextKindSchema = z.enum([
  "case-work",
  "organisation_administration",
  "thalamos_administration",
]) satisfies ZodType<SessionContextKind>;

/**
 * Where a session acts, and in what kind of work: what a person chooses, and
 * what the session stores.
 *
 * No roles, deliberately. They are read from the memberships on every request,
 * so a role granted or revoked mid-session takes effect without the person
 * choosing again.
 */
export type SessionContextChoiceUnbranded = {
  organisationId: OrganisationIdUnbranded;
  kind: SessionContextKind;
};

export const SessionContextChoiceSchema = z.object({
  organisationId: OrganisationIdSchema,
  kind: SessionContextKindSchema,
}) satisfies ZodType<SessionContextChoiceUnbranded>;

export type SessionContextChoice = z.infer<typeof SessionContextChoiceSchema>;

/** A choice resolved against the memberships: the roles it combines now. */
export type SessionContextUnbranded = SessionContextChoiceUnbranded & {
  organisationName?: string | undefined;
  roles: Role[];
  /**
   * Every organisation that is a member of this one, directly or through
   * another. What a member holds for a young person may carry up to this one.
   * Absent on a context that is only being offered.
   */
  memberOrganisationIds?: OrganisationIdUnbranded[] | undefined;
  /**
   * Whether this organisation reads what a finished assessment says about a
   * young person it reaches only through a member. Absent on a context that
   * is only being offered.
   */
  readsApprovedAssessmentsThroughMembers?: boolean | undefined;
};

export const SessionContextSchema = SessionContextChoiceSchema.extend({
  organisationName: z.string().min(1).max(255).optional(),
  roles: z.array(RoleSchema).min(1),
  memberOrganisationIds: z.array(OrganisationIdSchema).optional(),
  readsApprovedAssessmentsThroughMembers: z.boolean().optional(),
}) satisfies ZodType<SessionContextUnbranded>;

export type SessionContext = z.infer<typeof SessionContextSchema>;

/** Whether two contexts are the same choice, whatever roles each combines. */
export const sameSessionContext = (
  a: SessionContextChoice | null | undefined,
  b: SessionContextChoice | null | undefined,
): boolean =>
  a !== null &&
  a !== undefined &&
  b !== null &&
  b !== undefined &&
  a.organisationId === b.organisationId &&
  a.kind === b.kind;

/** A key naming a context uniquely among a user's, for a list or a set. */
export const sessionContextKey = (context: SessionContextChoice): string =>
  `${context.organisationId}:${context.kind}`;

/**
 * The organisation the session acts for, or `null` when no session context is
 * set.
 *
 * `null` rather than an empty string: an empty string is a valid string, so a
 * sentinel one invites a comparison that silently succeeds against another
 * absent value. Absence should not be spellable as data.
 */
export const sessionContextOrganisationId = (
  context: SessionContext | null,
): OrganisationId | null => (context === null ? null : context.organisationId);

/**
 * The longest a name may be, which is the width of the `users.name` column it is
 * stored in.
 */
const NAME_MAX_LENGTH = 255;

/**
 * A person's identity, and nothing about what they may do.
 *
 * Deliberately minimal: these are the fields every representation of a user
 * needs, so anything added here is added everywhere. What a user can act as
 * belongs on `CurrentUser`.
 */
export type UserUnbranded = {
  id: UserIdUnbranded;
  name: string;
  /**
   * The address the account is found by and reaches its owner at. The identity
   * provider vouches for it, so Haven never changes one; see
   * `docs/decisions/2026-09-08-the-email-address-is-not-havens-to-change.md`.
   */
  email: EmailAddressUnbranded;
};

/**
 * Deliberately unbranded, unlike `CurrentUser`. A brand here would be a *different*
 * brand, so a `CurrentUser` would stop being assignable to a `User` — and the only
 * reason this type exists is to be the narrower view a caller can take of one.
 *
 * `name` is looser here than `UserNameSchema`, which is what a person may *set*.
 * This one validates a row, and a row's name may have come from an identity
 * provider's claim rather than from anyone typing it — so reading an account back
 * must not fail over whitespace the account's owner never chose.
 */
export const UserSchema = z.object({
  id: UserIdSchema,
  name: z.string().min(1).max(NAME_MAX_LENGTH),
  email: EmailAddressSchema,
}) satisfies ZodType<UserUnbranded>;

export type User = z.infer<typeof UserSchema>;

/**
 * The signed-in user, as the API returns them: their identity plus the
 * memberships they hold and the context they are currently acting in.
 *
 * `memberships` defaults to empty rather than being required — a new account
 * genuinely belongs to nothing yet, and that is a state the frontend has to
 * render rather than a validation failure.
 */
export type CurrentUserUnbranded = UserUnbranded & {
  memberships: UserMembershipUnbranded[];
  sessionContext: SessionContextUnbranded | null;
};

/**
 * Branded, so holding a `CurrentUser` means one was parsed rather than assembled.
 * The ids inside carry their own brands; this one is about the envelope — a shape
 * that merely looks right cannot stand in for a validated response.
 */
export const CurrentUserSchema = UserSchema.extend({
  memberships: z.array(UserMembershipSchema).default([]),
  sessionContext: SessionContextSchema.nullable().default(null),
}).brand<"haven-common:CurrentUser">() satisfies ZodType<CurrentUserUnbranded>;

export type CurrentUser = z.infer<typeof CurrentUserSchema>;

/**
 * The body accepted when choosing a context.
 *
 * The display name and the roles carried on a context are dropped: both are the
 * server's to state, not the client's to assert. Accepting the name would let a
 * client relabel an organisation, and accepting the roles would let it claim
 * one it does not hold.
 */
export type SetSessionContextRequestUnbranded = SessionContextChoiceUnbranded;

export const SetSessionContextRequestSchema =
  SessionContextChoiceSchema.brand<"haven-common:SetSessionContextRequest">() satisfies ZodType<SetSessionContextRequestUnbranded>;

export type SetSessionContextRequest = z.infer<
  typeof SetSessionContextRequestSchema
>;

/**
 * Narrows a context to the fields a client may nominate.
 *
 * Parsed rather than asserted, because the branded type is the claim that a body
 * has been validated — building one by assertion would make the brand say
 * something untrue at the one place bodies are made.
 */
export const toSetSessionContextRequest = (
  context: SessionContextChoiceUnbranded,
): SetSessionContextRequest =>
  SetSessionContextRequestSchema.parse({
    organisationId: context.organisationId,
    kind: context.kind,
  });

/**
 * A name as a person may set it, which is stricter than what a stored row may
 * hold.
 *
 * Trimmed before it is measured, so a name of nothing but spaces is refused
 * rather than stored as one. A display name that renders blank is worse than a
 * missing one: every listing shows an unidentifiable row, and the account cannot
 * be told apart from the next.
 *
 * The messages are translation keys rather than sentences, following the field
 * schemas in `@thalamos/common`. This schema marks the field in the browser and
 * refuses the write on the server, and only one of those two has a locale to
 * render words from — so the wording lives with the client and the server reports
 * the key as it stands.
 */
export const UserNameSchema = z
  .string()
  .trim()
  .min(1, "profile.name.required")
  .max(NAME_MAX_LENGTH, "profile.name.tooLong") satisfies ZodType<string>;

/**
 * The fields of an account a request may change.
 *
 * The email address is deliberately absent, and its absence is the contract
 * rather than an omission: it is the identity the provider vouched for and the
 * key an account is found by, so accepting one here would let a request re-point
 * an account at a different person. See
 * `docs/decisions/2026-09-08-the-email-address-is-not-havens-to-change.md`.
 *
 * One shape whether someone is editing their own account or one they administer.
 * What differs between those two is who may do it, which is a question for the
 * authorisation rules — a second body type would have to be kept in step with
 * this one for no gain, and would drift.
 */
export type UpdateUserProfileRequestUnbranded = {
  name: string;
};

export const UpdateUserProfileRequestSchema = z
  .object({
    name: UserNameSchema,
  })
  .brand<"haven-common:UpdateUserProfileRequest">() satisfies ZodType<UpdateUserProfileRequestUnbranded>;

export type UpdateUserProfileRequest = z.infer<
  typeof UpdateUserProfileRequestSchema
>;
